import { useState, useEffect, useRef, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import { apiHelpers } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useDashboardStore } from '../../store/dashboardStore';
import ReactECharts from 'echarts-for-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import toast from 'react-hot-toast';
import {
  PaperAirplaneIcon,
  SparklesIcon,
  UserIcon,
  PlusIcon,
  ChevronDownIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  Bars3Icon
} from '@heroicons/react/24/outline';

// Chart types for the Add to Dashboard modal
const chartTypes = [
  { id: 'bar', name: 'Bar Chart', icon: '📊' },
  { id: 'line', name: 'Line Chart', icon: '📈' },
  { id: 'pie', name: 'Pie Chart', icon: '🥧' },
  { id: 'donut', name: 'Donut Chart', icon: '🍩' },
  { id: 'area', name: 'Area Chart', icon: '📉' },
  { id: 'scatter', name: 'Scatter Plot', icon: '🔵' },
  { id: 'heatmap', name: 'Heatmap', icon: '🔥' },
  { id: 'geo', name: 'Geo Map', icon: '🌍' },
  { id: 'table', name: 'Table', icon: '📋' },
  { id: 'kpi', name: 'KPI Card', icon: '🎯' }
];

export default function AIChat() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user, getCurrentCompanyId, isSuperUser } = useAuthStore();
  const messagesEndRef = useRef(null);
  
  const [message, setMessage] = useState('');
  const [provider, setProvider] = useState(user?.preferences?.defaultAIProvider || 'anthropic');
  const [model, setModel] = useState('');
  const [messages, setMessages] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(conversationId);
  
  // Mobile sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Add to Dashboard modal state
  const [showAddToDashboard, setShowAddToDashboard] = useState(false);
  const [selectedChartSuggestion, setSelectedChartSuggestion] = useState(null);
  const [selectedDashboard, setSelectedDashboard] = useState('');
  const [selectedChartType, setSelectedChartType] = useState('');
  const [chartName, setChartName] = useState('');
  const [isAddingChart, setIsAddingChart] = useState(false);
  
  const { addChart } = useDashboardStore();
  
  // Fetch available models
  const { data: modelsData } = useQuery({
    queryKey: ['aiModels'],
    queryFn: () => apiHelpers.getModels()
  });
  
  // Get current company ID for filtering (important for Super/Managing Admins)
  const currentCompanyId = getCurrentCompanyId();
  
  // Check if company is selected (for Super/Managing Admins)
  const noCompanySelected = isSuperUser() && !currentCompanyId;
  
  // Fetch conversations list - filter by selected company for Super/Managing Admins
  const { data: conversationsData, refetch: refetchConversations } = useQuery({
    queryKey: ['conversations', currentCompanyId],
    queryFn: () => apiHelpers.getConversations({ companyId: currentCompanyId }),
    enabled: !!currentCompanyId || !isSuperUser() // Regular users always enabled, super users need company selected
  });
  
  // Fetch current conversation
  const { data: conversationData } = useQuery({
    queryKey: ['conversation', currentConversationId],
    queryFn: () => apiHelpers.getConversation(currentConversationId),
    enabled: !!currentConversationId
  });
  
  // Fetch dashboards for Add to Dashboard modal
  const { data: dashboardsData } = useQuery({
    queryKey: ['dashboards', currentCompanyId],
    queryFn: () => apiHelpers.getDashboards({ companyId: currentCompanyId }),
    enabled: showAddToDashboard && !!currentCompanyId
  });
  
  useEffect(() => {
    if (conversationData?.data?.conversation?.messages) {
      setMessages(conversationData.data.conversation.messages);
    }
  }, [conversationData]);
  
  useEffect(() => {
    if (modelsData?.data?.providers?.[provider]) {
      setModel(modelsData.data.providers[provider].defaultModel);
    }
  }, [provider, modelsData]);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const chatMutation = useMutation({
    mutationFn: (data) => apiHelpers.chat(data),
    onSuccess: (response) => {
      const { response: aiResponse, conversationId: newConvId, chartSuggestion } = response.data;
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: aiResponse,
        chartSuggestion
      }]);
      
      if (!currentConversationId && newConvId) {
        setCurrentConversationId(newConvId);
        navigate(`/ai/${newConvId}`, { replace: true });
        refetchConversations();
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to send message');
    }
  });
  
  const deleteMutation = useMutation({
    mutationFn: (id) => apiHelpers.deleteConversation(id),
    onSuccess: (_, deletedId) => {
      toast.success('Conversation deleted');
      refetchConversations();
      
      // If we deleted the current conversation, start a new one
      if (currentConversationId === deletedId) {
        startNewConversation();
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete conversation');
    }
  });
  
  const handleDeleteConversation = (e, convId) => {
    e.stopPropagation(); // Prevent selecting the conversation
    if (window.confirm('Are you sure you want to delete this conversation?')) {
      deleteMutation.mutate(convId);
    }
  };
  
  const handleSend = () => {
    if (!message.trim() || chatMutation.isPending) return;
    
    const userMessage = message.trim();
    setMessage('');
    
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    
    chatMutation.mutate({
      message: userMessage,
      provider,
      model,
      conversationId: currentConversationId,
      companyId: currentCompanyId
    });
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  const handleAddToDashboard = (chartSuggestion) => {
    setSelectedChartSuggestion(chartSuggestion);
    setSelectedChartType(chartSuggestion.type || 'bar');
    setChartName(chartSuggestion.name || 'AI Generated Chart');
    setSelectedDashboard('');
    setShowAddToDashboard(true);
  };
  
  const handleConfirmAddToDashboard = async () => {
    if (!selectedDashboard || !selectedChartType || !chartName.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setIsAddingChart(true);
    
    try {
      // Create chart from AI suggestion
      const chartData = {
        name: chartName.trim(),
        type: selectedChartType,
        dashboardId: selectedDashboard,
        query: selectedChartSuggestion.query || '',
        description: `Generated by AI Assistant`,
        chartConfig: selectedChartSuggestion.config || {},
        aiGenerated: true
      };
      
      // If there's a data source, include it
      if (selectedChartSuggestion.dataSourceId) {
        chartData.dataSourceId = selectedChartSuggestion.dataSourceId;
      }
      
      await addChart(chartData);
      
      toast.success('Chart added to dashboard!');
      setShowAddToDashboard(false);
      setSelectedChartSuggestion(null);
    } catch (error) {
      console.error('Failed to add chart:', error);
      toast.error(error.response?.data?.error || 'Failed to add chart to dashboard');
    } finally {
      setIsAddingChart(false);
    }
  };
  
  const handleCloseAddToDashboard = () => {
    setShowAddToDashboard(false);
    setSelectedChartSuggestion(null);
    setSelectedDashboard('');
    setSelectedChartType('');
    setChartName('');
  };
  
  const startNewConversation = () => {
    setMessages([]);
    setCurrentConversationId(null);
    navigate('/ai');
  };
  
  const conversations = conversationsData?.data?.data || [];
  const models = modelsData?.data?.models || [];
  const providers = modelsData?.data?.providers || {};
  
  // Handle conversation selection on mobile (close sidebar after selection)
  const handleConversationSelect = (convId) => {
    setCurrentConversationId(convId);
    navigate(`/ai/${convId}`);
    setSidebarOpen(false); // Close sidebar on mobile after selection
  };

  const handleNewChat = () => {
    startNewConversation();
    setSidebarOpen(false); // Close sidebar on mobile
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex animate-fade-in relative">
      {/* Mobile sidebar backdrop */}
      <Transition
        show={sidebarOpen}
        as={Fragment}
        enter="transition-opacity ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      </Transition>

      {/* Sidebar - Hidden on mobile, shown as overlay when toggled */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-72 bg-white dark:bg-surface-900 border-r border-surface-200 dark:border-surface-800 flex flex-col
        transform transition-transform duration-300 ease-in-out
        lg:relative lg:translate-x-0 lg:z-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Mobile sidebar header */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b border-surface-200 dark:border-surface-800">
          <span className="font-display font-semibold text-surface-900 dark:text-white">Conversations</span>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
          >
            <XMarkIcon className="w-5 h-5 text-surface-500" />
          </button>
        </div>

        <div className="p-4">
          <button onClick={handleNewChat} className="btn-primary w-full">
            <PlusIcon className="w-5 h-5 mr-2" />
            New Chat
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto px-2">
          {noCompanySelected ? (
            <div className="text-center py-8 px-4">
              <p className="text-surface-500 text-sm">
                Select a company to view conversations
              </p>
            </div>
          ) : conversations.length === 0 ? (
            <p className="text-center text-surface-500 text-sm py-8">
              No conversations yet
            </p>
          ) : (
            <div className="space-y-1">
              {conversations.map(conv => (
                <div
                  key={conv._id}
                  className={`group flex items-center rounded-lg transition-colors ${
                    currentConversationId === conv._id
                      ? 'bg-primary-100 dark:bg-primary-900'
                      : 'hover:bg-surface-100 dark:hover:bg-surface-800'
                  }`}
                >
                  <button
                    onClick={() => handleConversationSelect(conv._id)}
                    className={`flex-1 text-left px-3 py-2 text-sm truncate ${
                      currentConversationId === conv._id
                        ? 'text-primary-700 dark:text-primary-300'
                        : 'text-surface-600 dark:text-surface-400'
                    }`}
                  >
                    <ChatBubbleLeftRightIcon className="w-4 h-4 inline mr-2" />
                    {conv.title}
                  </button>
                  <button
                    onClick={(e) => handleDeleteConversation(e, conv._id)}
                    className="p-2 opacity-0 group-hover:opacity-100 lg:group-hover:opacity-100 transition-opacity text-surface-400 hover:text-red-500 dark:text-surface-500 dark:hover:text-red-400"
                    title="Delete conversation"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Model selector */}
        <div className="p-3 sm:p-4 border-b border-surface-200 dark:border-surface-800 flex items-center gap-2 sm:gap-4">
          {/* Mobile menu button */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 flex-shrink-0"
            aria-label="Open conversations"
          >
            <Bars3Icon className="w-5 h-5 text-surface-600 dark:text-surface-400" />
          </button>

          <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="input w-28 sm:w-40 text-sm sm:text-base"
            >
              {Object.entries(providers).map(([key, value]) => (
                <option key={key} value={key}>{value.name}</option>
              ))}
            </select>
            
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="input flex-1 sm:flex-none sm:w-56 text-sm sm:text-base min-w-0"
            >
              {models
                .filter(m => m.provider === provider)
                .map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
            </select>
          </div>
        </div>
        
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-4 sm:space-y-6">
          {noCompanySelected ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-4">
                <SparklesIcon className="w-7 h-7 sm:w-8 sm:h-8 text-amber-600 dark:text-amber-400" />
              </div>
              <h2 className="text-lg sm:text-xl font-display font-bold text-surface-900 dark:text-white mb-2">
                Select a Company
              </h2>
              <p className="text-sm sm:text-base text-surface-500 dark:text-surface-400 max-w-md">
                Please select a company from the top navigation bar to start chatting with the AI Assistant.
              </p>
            </div>
          ) : messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mb-4">
                <SparklesIcon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <h2 className="text-lg sm:text-xl font-display font-bold text-surface-900 dark:text-white mb-2">
                AI Data Assistant
              </h2>
              <p className="text-sm sm:text-base text-surface-500 dark:text-surface-400 max-w-md">
                Ask me to analyze your data, create charts, find trends, or generate insights.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 mt-6 sm:mt-8 max-w-lg w-full">
                {[
                  'Show me a summary of sales by region',
                  'Create a line chart of monthly revenue',
                  'What are the top 10 products by sales?',
                  'Find any anomalies in the data'
                ].map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => setMessage(suggestion)}
                    className="text-left p-3 sm:p-4 rounded-xl border border-surface-200 dark:border-surface-700 text-xs sm:text-sm text-surface-600 dark:text-surface-400 hover:border-primary-500 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg, i) => (
              <div key={i} className={`flex gap-2 sm:gap-4 ${msg.role === 'assistant' ? '' : 'flex-row-reverse'}`}>
                <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.role === 'assistant' 
                    ? 'bg-gradient-to-br from-primary-500 to-accent-500' 
                    : 'bg-surface-200 dark:bg-surface-700'
                }`}>
                  {msg.role === 'assistant' 
                    ? <SparklesIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                    : <UserIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-surface-600 dark:text-surface-400" />
                  }
                </div>
                
                <div className={`max-w-[85%] sm:max-w-3xl ${msg.role === 'user' ? 'text-right' : ''}`}>
                  <div className={`inline-block px-3 py-2 sm:px-4 sm:py-3 rounded-2xl ${
                    msg.role === 'assistant'
                      ? 'bg-surface-100 dark:bg-surface-800 text-surface-900 dark:text-white'
                      : 'bg-primary-600 text-white'
                  }`}>
                    {msg.role === 'assistant' ? (
                      <div className="prose prose-sm dark:prose-invert max-w-none prose-headings:my-2 prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-strong:text-primary-600 dark:prose-strong:text-primary-400 prose-code:bg-surface-200 dark:prose-code:bg-surface-700 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-pre:bg-surface-200 dark:prose-pre:bg-surface-900 prose-pre:text-surface-800 dark:prose-pre:text-surface-200 prose-pre:border prose-pre:border-surface-300 dark:prose-pre:border-surface-600 prose-pre:rounded-lg prose-pre:overflow-x-auto [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-inherit text-sm sm:text-base">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap text-sm sm:text-base">{msg.content}</p>
                    )}
                  </div>
                  
                  {/* Chart suggestion */}
                  {msg.chartSuggestion && (
                    <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-surface-50 dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700">
                      <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-2 sm:mb-3">
                        <ChartBarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-primary-500" />
                        <span className="font-medium text-sm sm:text-base text-surface-900 dark:text-white">
                          Chart: {msg.chartSuggestion.name || msg.chartSuggestion.type}
                        </span>
                        <span className="text-xs sm:text-sm text-surface-500">
                          ({msg.chartSuggestion.type})
                        </span>
                      </div>
                      {msg.chartSuggestion.query && (
                        <div className="mb-2 sm:mb-3 p-2 bg-surface-100 dark:bg-surface-900 rounded text-xs font-mono text-surface-600 dark:text-surface-400 overflow-x-auto">
                          <code>{msg.chartSuggestion.query.substring(0, 100)}...</code>
                        </div>
                      )}
                      <button 
                        onClick={() => handleAddToDashboard(msg.chartSuggestion)}
                        className="btn-primary btn-sm text-xs sm:text-sm"
                      >
                        <PlusIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                        Add to Dashboard
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          
          {chatMutation.isPending && (
            <div className="flex gap-2 sm:gap-4">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <SparklesIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
              </div>
              <div className="bg-surface-100 dark:bg-surface-800 rounded-2xl px-3 py-2 sm:px-4 sm:py-3">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-surface-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-surface-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-surface-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input */}
        <div className="p-3 sm:p-4 border-t border-surface-200 dark:border-surface-800">
          <div className="flex gap-2 sm:gap-4">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={noCompanySelected ? "Select a company to start chatting..." : "Ask anything about your data..."}
              rows={1}
              className="flex-1 input resize-none text-sm sm:text-base"
              disabled={noCompanySelected}
            />
            <button
              onClick={handleSend}
              disabled={!message.trim() || chatMutation.isPending || noCompanySelected}
              className="btn-primary px-3 sm:px-4 flex-shrink-0"
            >
              <PaperAirplaneIcon className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Add to Dashboard Modal */}
      <Transition appear show={showAddToDashboard} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleCloseAddToDashboard}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-surface-900 shadow-2xl transition-all">
                  {/* Header */}
                  <div className="flex items-center justify-between p-6 border-b border-surface-200 dark:border-surface-800">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                        <ChartBarIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <Dialog.Title className="text-lg font-display font-semibold text-surface-900 dark:text-white">
                        Add Chart to Dashboard
                      </Dialog.Title>
                    </div>
                    <button 
                      onClick={handleCloseAddToDashboard}
                      className="p-1 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
                    >
                      <XMarkIcon className="w-5 h-5 text-surface-500" />
                    </button>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6 space-y-5">
                    {/* Chart Name */}
                    <div>
                      <label className="label">Chart Name</label>
                      <input
                        type="text"
                        value={chartName}
                        onChange={(e) => setChartName(e.target.value)}
                        placeholder="Enter chart name"
                        className="input"
                      />
                    </div>
                    
                    {/* Select Dashboard */}
                    <div>
                      <label className="label">Select Dashboard</label>
                      {noCompanySelected ? (
                        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                          <p className="text-sm text-amber-700 dark:text-amber-300">
                            <strong>No company selected.</strong> Please select a company from the top navigation bar first to see available dashboards.
                          </p>
                        </div>
                      ) : (
                        <>
                          <select
                            value={selectedDashboard}
                            onChange={(e) => setSelectedDashboard(e.target.value)}
                            className="input"
                          >
                            <option value="">Choose a dashboard...</option>
                            {dashboardsData?.data?.data?.map(dashboard => (
                              <option key={dashboard._id} value={dashboard._id}>
                                {dashboard.name}
                              </option>
                            ))}
                          </select>
                          {(!dashboardsData?.data?.data || dashboardsData.data.data.length === 0) && (
                            <p className="text-sm text-surface-500 mt-1">
                              No dashboards found. Create a dashboard first.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                    
                    {/* Chart Type Selection */}
                    <div>
                      <label className="label">Chart Type</label>
                      <p className="text-sm text-surface-500 mb-2">
                        AI suggested: <span className="font-medium text-primary-600">{selectedChartSuggestion?.type || 'bar'}</span>
                      </p>
                      <div className="grid grid-cols-5 gap-2">
                        {chartTypes.map(type => (
                          <button
                            key={type.id}
                            onClick={() => setSelectedChartType(type.id)}
                            className={`p-3 rounded-xl border-2 text-center transition-all hover:border-primary-500 ${
                              selectedChartType === type.id
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-950'
                                : 'border-surface-200 dark:border-surface-700'
                            }`}
                          >
                            <span className="text-xl block">{type.icon}</span>
                            <span className="text-xs font-medium text-surface-600 dark:text-surface-400 mt-1 block">
                              {type.name.split(' ')[0]}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* SQL Preview */}
                    {selectedChartSuggestion?.query && (
                      <div>
                        <label className="label">SQL Query</label>
                        <div className="p-3 bg-surface-100 dark:bg-surface-800 rounded-lg text-xs font-mono text-surface-600 dark:text-surface-400 overflow-x-auto max-h-32 overflow-y-auto">
                          <pre className="whitespace-pre-wrap">{selectedChartSuggestion.query}</pre>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Footer */}
                  <div className="flex items-center justify-end gap-3 p-6 border-t border-surface-200 dark:border-surface-800">
                    <button
                      onClick={handleCloseAddToDashboard}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmAddToDashboard}
                      disabled={!selectedDashboard || !selectedChartType || !chartName.trim() || isAddingChart || noCompanySelected}
                      className="btn-primary"
                    >
                      {isAddingChart ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      ) : (
                        <CheckIcon className="w-5 h-5 mr-2" />
                      )}
                      Add to Dashboard
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

